import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import type { Dispatch, FC } from 'react';
import { useCallback, useEffect, useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

import type {
	ArchiveLongTaskStatusExperimental as ArchiveLongTaskStatusExperimentalResponse,
	ArchiveLongTaskStatusExperimentalVariables,
	ArchiveLongTaskStatusExperimental_experimentalLongTask as ArchiveLongTask,
} from './__types__/ArchiveLongTaskStatusExperimental';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { ArchiveLongTaskStatusExperimental } from './ArchiveLongTaskStatusExperimental.experimentalgraphql';
// eslint-disable-next-line import/no-cycle
import { getExperienceName } from './ArchiveProgressReport';

const POLL_INTERVAL_MS = 500; // 0.5 seconds
const POLL_TIMEOUT_MS = 60000; // 60 seconds

export type ArchivePollStatusUpdateParams = {
	archivingSuccessPages: string[];
	percentageComplete: number;
};

type ArchivePollStatusProps = {
	longTaskId: string;
	onError: Dispatch<Error>;
	onStatusUpdate: Dispatch<ArchivePollStatusUpdateParams>;
	isArchive: boolean;
	isBulk: boolean;
	pollTimeout?: number;
	featureName: string;
	processedPageIds: string[];
};

export const ArchivePollStatus: FC<ArchivePollStatusProps> = ({
	longTaskId,
	onError,
	onStatusUpdate,
	isArchive,
	pollTimeout = POLL_TIMEOUT_MS,
	isBulk,
	featureName,
	processedPageIds,
}: ArchivePollStatusProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, error, stopPolling } = useQuery<
		ArchiveLongTaskStatusExperimentalResponse,
		ArchiveLongTaskStatusExperimentalVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
		ArchiveLongTaskStatusExperimental,
		{
			fetchPolicy: 'network-only',
			pollInterval: POLL_INTERVAL_MS,
			variables: { taskId: longTaskId },
		},
	);

	const experienceTracker = useContext(ExperienceTrackerContext);

	const handleError = useCallback(
		(error: Error, pageIds?: string[]) => {
			experienceTracker.stopOnError({
				name: getExperienceName(isArchive),
				error,
				attributes: {
					featureName,
				},
			});

			if (isArchive && isBulk) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'bulkArchive',
						source: featureName,
						attributes: {
							pageCount: pageIds?.length || processedPageIds.length,
							errorMessage: error.message,
						},
					},
				}).fire();
			}

			onError(error);
			stopPolling();
		},
		[
			experienceTracker,
			isArchive,
			isBulk,
			featureName,
			onError,
			stopPolling,
			createAnalyticsEvent,
			processedPageIds.length,
		],
	);

	useEffect(() => {
		// stop polling and report error if we've timed out
		const timeout = setTimeout(() => {
			handleError(new Error('Timed out while polling server for status of page archival process'));
		}, pollTimeout);
		// clean up on unmount
		return () => clearTimeout(timeout);
	}, [handleError, pollTimeout]);

	// The callback (prop) functions that affect the parent component's state cannot be called during
	// the rendering process, and instead must be in a "useEffect()" hook, which are called after the
	// rendering has completed.

	// Query error processing:
	useEffect(() => {
		error && handleError(error);
	}, [error, handleError]);

	// onStatusUpdate callback processing:
	useEffect(() => {
		const longTask: ArchiveLongTask | null = (data && data.experimentalLongTask) || null;

		if (longTask) {
			const percentageComplete = longTask.percentageComplete || 0;
			const { successful } = longTask;

			let pageIds: string[] = [];
			let pageIdProcessingError: Error | null = null;

			// The longtask API doesn't allow custom formatted data to be returned
			// yet, so for now the only way to pass/retrieve the IDs of the pages that
			// have been successfully archived is via the messages field. We have to
			// parse the text for the comma-delimited array of page IDs in a string
			// that looks like this:
			// "PageArchivedList :: [123,345,456]"
			// "PageUnArchivedList :: [123,345,456]"
			// TODO: better way to receive pageIds is using args instead of translation string.

			// regex to match list type label
			const listTypeRegex = new RegExp(`^(PageUnArchivedList|PageArchivedList)`, 'gi');
			// regex to match pageIds
			const pageIdRegex = new RegExp(`([\\d*]+)`, 'g');

			try {
				const { messages } = longTask;

				for (let i = messages.length - 1; i >= 0; --i) {
					const { translation } = messages[i];
					const listType = translation.match(listTypeRegex);
					if (!listType) {
						// not a valid message to parse pageIds so skip
						break;
					}

					const pageIdArray = translation.match(pageIdRegex);

					if (pageIdArray && pageIdArray.length) {
						// able to parse pageIds so break
						pageIds = pageIdArray;
						break;
					}
				}
			} catch (e) {
				// backend did not send data in the expected format
				pageIdProcessingError = e;
			}

			// If no errors have occured, report the status
			if (!pageIdProcessingError) {
				// If everything has completed, stop polling
				if (successful && percentageComplete === 100) {
					stopPolling();
				}

				onStatusUpdate({
					archivingSuccessPages: pageIds,
					percentageComplete,
				});
			} else {
				handleError(pageIdProcessingError, pageIds);
			}
		}
	}, [data, handleError, onStatusUpdate, stopPolling]);

	// Regular rendering:
	return null;
};
